/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { cx, jsx } from '@compiled/react';

import type { IconProp } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Pressable from '@atlaskit/primitives/pressable';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

const styles = cssMap({
	base: {
		// default Atlaskit button styles
		display: 'inline-flex',
		boxSizing: 'border-box',
		width: '45px',
		maxWidth: '100%',
		position: 'relative',
		alignItems: 'center', // override default: baseline
		justifyContent: 'center',
		columnGap: token('space.050'),
		borderRadius: token('radius.small'),
		borderWidth: 0,
		flexShrink: 0,
		height: '56px',
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		textAlign: 'center',
		transition: 'background 0.1s ease-out',
		verticalAlign: 'middle',
	},
	iconButton: {
		// default Atlaskit icon button styles
		paddingBlock: token('space.100'),
		paddingInline: token('space.100'),
	},
	disabled: {
		// default Atlaskit button styles
		cursor: 'not-allowed',
		backgroundColor: token('color.background.neutral.subtle'), // override default: color.background.disabled
		color: token('color.text.disabled'),
	},
	subtle: {
		backgroundColor: token('color.background.neutral.subtle'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
	iconScaleStyles: {
		transform: 'scale(1.2)',
	},
});

export type AudioControlIconButtonProps = {
	Icon: IconProp;
	isDisabled?: boolean;
	label: string;
	onClick?: () => void;
	testId?: string;
	tooltip: string;
};

export type AudioControlTriggerButtonProps = {
	children: React.ReactNode;
	isDisabled?: boolean;
	label: string;
	onClick?: () => void;
	testId?: string;
	triggerProps: React.ButtonHTMLAttributes<HTMLButtonElement>;
	triggerRef: React.Ref<any>;
};

export const AudioControlIconButton = ({
	onClick = () => {},
	Icon,
	tooltip,
	label,
	testId,
	isDisabled,
}: AudioControlIconButtonProps): React.ReactNode => {
	return (
		<Tooltip content={!isDisabled && tooltip}>
			<Pressable
				onClick={onClick}
				isDisabled={isDisabled}
				xcss={cx(styles.base, styles.iconButton, isDisabled ? styles.disabled : styles.subtle)}
				testId={testId}
				aria-label={label}
			>
				<span css={styles.iconScaleStyles}>
					<Icon
						label={label}
						color={isDisabled ? token('color.icon.disabled') : token('color.icon.subtle')}
					/>
				</span>
			</Pressable>
		</Tooltip>
	);
};

export const AudioControlTriggerButton = ({
	onClick = () => {},
	testId,
	label,
	isDisabled,
	children,
	triggerRef,
	triggerProps,
}: AudioControlTriggerButtonProps): React.ReactNode => {
	return (
		<Pressable
			onClick={onClick}
			isDisabled={isDisabled}
			xcss={cx(styles.base, isDisabled ? styles.disabled : styles.subtle)}
			testId={testId}
			ref={triggerRef}
			aria-label={label}
			{...triggerProps}
		>
			{children}
		</Pressable>
	);
};
